import type { TaskLikeObject } from '@frontend/api-client';
import { getTaskSubtitle, isTaskCloseableByMember } from '@frontend/api-client';
import { match } from '@frontend/duck-tape';
import { useMemo } from '@frontend/react';
import type { ApiClientHook, HandleMutationFn } from '@frontend/react-domain/utils/types';

type UseTaskLikeCardProps = {
  handleMutation: HandleMutationFn;
  markAsDoneDeployDate: MaybeNull<DateISOString>;
  nextTriggerAt?: DateTimeISOString;
  taskLikeObject: TaskLikeObject;
  updateTask: ReturnType<ApiClientHook['useUpdateTaskMutation']>[0];
};

/** Hook that extracts shared logic for TaskLikeCard components across platforms. */
export const useTaskLikeCard = ({
  handleMutation,
  markAsDoneDeployDate,
  nextTriggerAt,
  taskLikeObject,
  updateTask,
}: UseTaskLikeCardProps) => {
  const { subtitle, title } = useMemo(
    () =>
      match<typeof taskLikeObject, { subtitle: string; title: string }>(taskLikeObject)
        .with({ __typename: 'Task' }, (task) => {
          return {
            subtitle: getTaskSubtitle(task, markAsDoneDeployDate, nextTriggerAt),
            title: task.name,
          };
        })
        .with({ __typename: 'DraftTask' }, (draftTask) => ({
          subtitle: 'Tap to complete draft',
          title: draftTask.name,
        }))
        .exhaustive(),
    [taskLikeObject, markAsDoneDeployDate, nextTriggerAt],
  );

  const isUnread = match(taskLikeObject)
    .with({ __typename: 'Task' }, (task) => task.isUnread)
    .with({ __typename: 'DraftTask' }, () => false)
    .exhaustive();

  const isTitleEmphasized = match(taskLikeObject)
    .with({ __typename: 'Task' }, (task) => task.isUnread)
    .with({ __typename: 'DraftTask' }, () => false)
    .exhaustive();

  const isCloseable = match(taskLikeObject)
    .with({ __typename: 'Task' }, isTaskCloseableByMember)
    .with({ __typename: 'DraftTask' }, () => false)
    .exhaustive();

  const isRecurrenceInstance = match(taskLikeObject)
    .with({ __typename: 'Task' }, (task) => !!task.recurrenceDate)
    .with({ __typename: 'DraftTask' }, () => false)
    .exhaustive();

  const onUpdateTaskReadStatus = async () => {
    return match(taskLikeObject)
      .with({ __typename: 'Task' }, (task) =>
        handleMutation({
          errorMessage: 'Failed to mark task as unread',
          mutation: async () => updateTask({ data: { isUnread: !task.isUnread }, id: task.id }),
          showSuccessToast: false,
        }),
      )
      .with({ __typename: 'DraftTask' }, () => undefined)
      .exhaustive();
  };

  const canUpdateTaskReadStatus = match(taskLikeObject)
    .with({ __typename: 'Task' }, isTaskCloseableByMember)
    .with({ __typename: 'DraftTask' }, () => false)
    .exhaustive();

  return {
    canUpdateTaskReadStatus,
    isCloseable,
    isRecurrenceInstance,
    isTitleEmphasized,
    isUnread,
    onUpdateTaskReadStatus,
    subtitle,
    title,
  };
};
